import {useEffect, useRef} from 'react'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {DetailsPageWrapper} from '@/components/ui/details-atoms/DetailsAtoms'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {useVerifyAccountEmail} from '@/features/account/services/useVerifyAccountEmail'
import {useAccountStore} from '@/features/account/store/useAccountStore'
import {useTranslation} from '@/translations/i18n'
import {routes} from '@/utilities/constants/routes'
import {Navigate} from 'react-router-dom'
import {useEmailVerificationParams} from '@/hooks/useEmailVerificationParams'
import {toast} from '@/utilities/toast/toast'

// rendering validation
export const EmailVerificationRoute = () => {
    const {t} = useTranslation()
    const {userId, token} = useEmailVerificationParams()

    if (!userId || !token)
        return (
            <DetailsPageWrapper isSuccess={false}>
                <ErrorBox icon={<AlertCircleIcon />} title={t('errors:invalid_link')} />
            </DetailsPageWrapper>
        )

    return <EmailVerifier userId={userId} token={token} />
}

// business logic
const EmailVerifier = ({userId, token}: {userId: string; token: string}) => {
    const {t} = useTranslation()
    const {setEmailVerificationStatus, setIsAccountModalOpened} = useAccountStore()
    const hasExecuted = useRef(false)

    const verifyEmail = useVerifyAccountEmail(
        {
            urlParams: {userId},
            params: {token}
        },
        {
            onMutate: () => setIsAccountModalOpened(true),
            onError: () => {
                toast.error(t('account:email_verification_failed'))
                setEmailVerificationStatus('error')
            },
            onSuccess: () => {
                toast.success(t('account:email_verified'))
                setEmailVerificationStatus('success')
            }
        }
    )

    useEffect(() => {
        // Prevent multiple executions of the mutation due to Strict Mode's intentional double mounting in development
        if (!hasExecuted.current) {
            verifyEmail.mutate()
            setEmailVerificationStatus('pending')
            hasExecuted.current = true
        }
    }, [hasExecuted])

    return <Navigate to={routes.DASHBOARD.path} replace={true} />
}
